// src/components/CompanyProjectAccessBanner.jsx
//
// Shown to company account owners viewing a project via company-wide visibility
// (isCompanyOwnerOfProject Firestore rule) rather than an actual invite - lets them ask to
// join as a collaborator. Self-contained (owns its own request/confirm state) so it can be
// dropped into any page that already knows canEdit/isCompanyOwner, without each page having
// to re-fetch permissions itself. Used by withProjectPermissions.jsx (for sub-pages) and
// ProjectDashboard.jsx (the actual project landing page, which manages its own permissions
// state and was never wrapped by that HOC).

import React, { useCallback, useState } from 'react';
import { Eye } from 'lucide-react';
import collaborationService from '../services/collaborationService';

export default function CompanyProjectAccessBanner({ projectId, canEdit, isCompanyOwner }) {
  const [requestState, setRequestState] = useState('idle'); // idle | sending | sent | error
  const [showRequestConfirm, setShowRequestConfirm] = useState(false);

  const handleRequestAccess = useCallback(async () => {
    setShowRequestConfirm(false);
    setRequestState('sending');
    const result = await collaborationService.requestProjectAccess(projectId);
    setRequestState(result.success ? 'sent' : 'error');
  }, [projectId]);

  if (canEdit || !isCompanyOwner) return null;

  return (
    <>
      <div style={{
        padding: '12px 24px',
        background: '#f0f9ff',
        borderBottom: '1px solid var(--branding-app-primary)',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        color: '#1e40af',
        fontSize: 13,
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Eye size={16} strokeWidth={2.25} style={{ flexShrink: 0 }} />
          <span>View only</span>
        </div>
        {requestState === 'sent' ? (
          <span style={{ fontWeight: 600 }}>Request sent</span>
        ) : (
          <button
            onClick={() => setShowRequestConfirm(true)}
            disabled={requestState === 'sending'}
            style={{
              background: 'var(--branding-app-primary)',
              color: 'var(--branding-app-primary-fg)',
              border: 'none',
              borderRadius: 6,
              padding: '6px 14px',
              fontWeight: 600,
              fontSize: 12,
              cursor: requestState === 'sending' ? 'default' : 'pointer',
              flexShrink: 0,
              boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
            }}
          >
            {requestState === 'sending' ? 'Sending…' : requestState === 'error' ? 'Retry request' : 'Request access'}
          </button>
        )}
      </div>
      {showRequestConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
        }}>
          <div style={{ background: 'var(--cc-card-bg)', borderRadius: 12, padding: 32, minWidth: 340, maxWidth: 420, boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>
            <h3 style={{ marginTop: 0, marginBottom: 12, color: 'var(--cc-text-primary)' }}>
              Ask to join as collaborator?
            </h3>
            <p style={{ margin: '0 0 20px', color: 'var(--cc-text-secondary)', fontSize: 14, lineHeight: 1.5 }}>
              The project owner will need to approve this before you can edit.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowRequestConfirm(false)}
                style={{ background: 'var(--cc-cancel-bg)', color: 'var(--cc-text-primary)', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 500, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleRequestAccess}
                style={{ background: 'var(--branding-app-primary)', color: 'var(--branding-app-primary-fg)', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer' }}
              >
                Request
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
